/**
 * src/mcp/api/mcp.js
 *
 * Migrated off raw fetch() onto the shared axios instance in
 * src/api/client.js. Previously this module sent completely
 * unauthenticated requests (its own hardcoded API_BASE, no Authorization
 * header at all) — it now carries the same auth Bearer token,
 * X-User-Email header, and 401 handling as every other module.
 *
 * EventSource (SSE) and WebSocket streaming can't go through axios, so
 * streamQuery() and createWebSocket() still build their own URL — but now
 * from the same BASE_URL client.js resolves, instead of a separately
 * hardcoded '/api/mcp' constant that could drift out of sync.
 */
import { apiGet, apiPost, apiDelete, getApiError, BASE_URL } from "../../api/client";

const MODULE_BASE = "/mcp";

/**
 * MCP API client
 */
export const mcpApi = {
  // ============== Health & Status ==============

  async getHealth() {
    try {
      return await apiGet(`${MODULE_BASE}/health`);
    } catch (e) {
      throw new Error(getApiError(e) || "Health check failed");
    }
  },

  async getStatus() {
    try {
      return await apiGet(`${MODULE_BASE}/status`);
    } catch (e) {
      throw new Error(getApiError(e) || "Status check failed");
    }
  },

  // ============== Server Management ==============

  async connectStdioServer(name, command, env = null) {
    try {
      return await apiPost(`${MODULE_BASE}/servers/connect/stdio`, { name, command, env });
    } catch (e) {
      throw new Error(getApiError(e) || "Connection failed");
    }
  },

  async connectSSEServer(name, url, headers = null) {
    try {
      return await apiPost(`${MODULE_BASE}/servers/connect/sse`, { name, url, headers });
    } catch (e) {
      throw new Error(getApiError(e) || "Connection failed");
    }
  },

  async getServers() {
    try {
      return await apiGet(`${MODULE_BASE}/servers`);
    } catch {
      throw new Error("Failed to fetch servers");
    }
  },

  async disconnectServer(name) {
    try {
      return await apiDelete(`${MODULE_BASE}/servers/${name}`);
    } catch {
      throw new Error("Disconnect failed");
    }
  },

  async disconnectAllServers() {
    try {
      return await apiPost(`${MODULE_BASE}/servers/disconnect-all`);
    } catch {
      throw new Error("Disconnect all failed");
    }
  },

  // ============== Tools ==============

  async getTools() {
    try {
      return await apiGet(`${MODULE_BASE}/tools`);
    } catch {
      throw new Error("Failed to fetch tools");
    }
  },

  async getServerTools(serverName) {
    try {
      return await apiGet(`${MODULE_BASE}/tools/${serverName}`);
    } catch {
      throw new Error("Failed to fetch server tools");
    }
  },

  async invokeTool(server, tool, args) {
    try {
      return await apiPost(`${MODULE_BASE}/tools/invoke`, { server, tool, arguments: args });
    } catch (e) {
      throw new Error(getApiError(e) || "Tool invocation failed");
    }
  },

  // ============== Agent Query ==============

  async query(queryText, options = {}) {
    try {
      return await apiPost(`${MODULE_BASE}/query`, {
        query: queryText,
        include_db_context: options.includeDbContext || false,
        db_table: options.dbTable || null,
      });
    } catch (e) {
      throw new Error(getApiError(e) || "Query failed");
    }
  },

  // Stream query via SSE
  streamQuery(queryText) {
    const url = `${BASE_URL}${MODULE_BASE}/query/stream?query=${encodeURIComponent(queryText)}`;
    return new EventSource(url);
  },

  // ============== History ==============

  async getHistory() {
    try {
      return await apiGet(`${MODULE_BASE}/history`);
    } catch {
      throw new Error("Failed to fetch history");
    }
  },

  async clearHistory() {
    try {
      return await apiPost(`${MODULE_BASE}/history/clear`);
    } catch {
      throw new Error("Failed to clear history");
    }
  },

  // ============== Models ==============

  async getModels() {
    try {
      return await apiGet(`${MODULE_BASE}/models`);
    } catch {
      throw new Error("Failed to fetch models");
    }
  },

  async setModel(model) {
    try {
      return await apiPost(`${MODULE_BASE}/models`, { model });
    } catch {
      throw new Error("Failed to set model");
    }
  },

  // ============== WebSocket ==============

  createWebSocket() {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return new WebSocket(`${protocol}//${window.location.host}${BASE_URL}${MODULE_BASE}/ws`);
  },
};

export default mcpApi;
